import { useLingui } from "@lingui/react";
import { ApiDatafanProject, FailureApi } from "@smp/api";
import { Await } from "@smp/utils";
import { useInfiniteQuery } from "react-query";
import { UseInfiniteQueryOptions } from "react-query/types/react/types";

import {
  IDatafanPipeline,
  useDatafanPipelineAggregation,
} from "@/hooks/use_datafan/use_datafan_pipeline_aggregation";
import { useQueryDatafanProject } from "@/queries/datafan/use_query_datafan_project";
import { gscSendUserParams } from "@/utils";

const KEY = "datafan.pipelines";
const LIMIT = 10;

type Response = Await<ReturnType<typeof ApiDatafanProject.Pipelines.list>> & {
  count: number;
};

type ResponseSelect = {
  count: number;
  results: IDatafanPipeline[];
};

export const useQueryDatafanPipelines = (
  params?: {
    name?: string;
    source?: number[];
    id__in?: number[];
  },
  options?: Omit<
    UseInfiniteQueryOptions<Response, FailureApi, ResponseSelect>,
    "queryKey" | "queryFn" | "select"
  >,
) => {
  const aggregatePipeline = useDatafanPipelineAggregation();
  const { i18n } = useLingui();
  const lang = i18n.locale === "ru" ? "ru" : "en";
  const {
    data: project,
    error: projectError,
    isLoading: projectIsLoading,
    isFetching: projectIsFetching,
  } = useQueryDatafanProject({
    enabled: options?.enabled,
  });

  const { data, error, isLoading, ...spread } = useInfiniteQuery<
    Response,
    FailureApi,
    ResponseSelect
  >(
    [
      KEY,
      {
        ...params,
        projectId: project?.id,
      },
    ],
    ({ pageParam }) =>
      ApiDatafanProject.Pipelines.list(project?.id || 0, {
        filter: {
          name: params?.name,
          source: params?.source,
        },
        id__in: params?.id__in,
        lang,
        limit: params?.id__in?.length ? undefined : LIMIT,
        ...pageParam,
      }),
    {
      ...options,
      enabled: !!project && options?.enabled && !projectIsFetching,
      getNextPageParam: (data) => {
        if (!data.next) {
          return false;
        }

        return {
          offset: new URL(data.next).searchParams.get("offset"),
        };
      },
      select: (data) => ({
        ...data,
        pages: data.pages.map((page) => ({
          ...page,
          results: page.results.map(aggregatePipeline),
        })),
      }),
      onSuccess: (...args) => {
        const [data] = args;

        if (data.pages.length === 1) {
          gscSendUserParams({
            pipeline: data.pages[0].count,
          });
        }

        options?.onSuccess?.(...args);
      },
    },
  );

  return {
    data: data,
    error: error || projectError,
    isLoading: isLoading || projectIsLoading,
    ...spread,
  };
};

useQueryDatafanPipelines.KEY = KEY;
